import React, { useState } from 'react';
import {
  cssColorValue,
  desktopMestoBold,
  NavBar,
  NavBarList,
  screenMdMin,
  semanticColors,
  spacer32,
  TextLink,
} from '@spotify-internal/encore-web';
import styled from 'styled-components';

import { MastheadLinkList, MastheadLinkOrLinkListArray } from 'lib/sanity/types';
import { isMastheadLinkList } from 'utils/type-guards';
import useDropdown, { DropdownContext } from 'components/Masthead/Header/hooks/useDropdown';
import { NavBarPrimaryLinksPopover } from 'components/Masthead/Header/Popover/PrimaryLinks';
import { PopoverLink } from 'components/Masthead/Header/types';
import { PopoverTriggerButton } from 'components/Masthead/Header/Popover/TriggerButton';

const StyledNavBar = styled(NavBar)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  @media (max-width: ${screenMdMin}) {
    display: none;
  }
`;

const StyledNavBarList = styled(NavBarList)`
  gap: ${spacer32};
  line-height: 24px;
  margin: 0;
  overflow-x: visible;
  padding: 0;
  ${desktopMestoBold};
`;

const NavLink = styled(TextLink)`
  cursor: pointer;
  ${desktopMestoBold};

  && {
    text-decoration: none;

    &:active,
    &:hover,
    &:focus {
      text-decoration: none;
    }

    &:focus {
      color: ${cssColorValue(semanticColors.textBrightAccent)};
    }
  }
`;

const NavLinkList = ({ linkListEntry: { label, entries = [] } }: { linkListEntry: MastheadLinkList }) => {
  const { handlers, open } = useDropdown(label);
  const links = entries.map<PopoverLink>(linkListItem => {
    const { href, target, title: linkText } = linkListItem.link;
    return {
      href,
      linkText,
      level: linkListItem?.nestingLevel,
      target,
    };
  });

  return (
    <NavBarPrimaryLinksPopover
      isOpen={open}
      links={links}
      onMouseEnter={handlers.handleOnMouseEnter}
      onMouseLeave={handlers.handleOnMouseLeave}
    >
      <PopoverTriggerButton open={open} onKeyDown={handlers.handleOnKeyDown} onMouseDown={handlers.handleOnMouseDown}>
        {label}
      </PopoverTriggerButton>
    </NavBarPrimaryLinksPopover>
  );
};

type Props = {
  primaryLinks?: MastheadLinkOrLinkListArray;
};

export const NavBarPrimaryLinks = ({ primaryLinks }: Props) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  if (!primaryLinks) {
    return null;
  }

  return (
    <StyledNavBar
      list={
        <nav dir="ltr">
          <StyledNavBarList>
            <DropdownContext.Provider value={{ activeDropdown, setActiveDropdown }}>
              {primaryLinks?.map((item, index) => {
                if (isMastheadLinkList(item)) {
                  return (
                    <li key={item.label + index}>
                      <NavLinkList linkListEntry={item} />
                    </li>
                  );
                }
                return (
                  <li key={index}>
                    <NavLink href={item.href} target={item.target}>
                      {item.title}
                    </NavLink>
                  </li>
                );
              })}
            </DropdownContext.Provider>
          </StyledNavBarList>
        </nav>
      }
    />
  );
};
